import { Component, Input } from '@angular/core';
import {
  AuthenticationService,
  AzzurroHubDataType,
  checkFwVersion,
  ConnextData,
  getDeviceIconIdentifier,
  isNotNullOrUndefined,
  MonitorPrefEnumType,
  PlantMonitoringEnumType,
  ProductModelService,
  RoleService,
  System,
  UpdatePlantSettingsInCertifiedPlantInput,
  UpdatePlantSettingsInCertifiedPlantMutation,
  UserPermissions,
} from 'ngx-core-services';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { IonicModule } from '@ionic/angular';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  AlertsCounterModule,
  HeaderOptionsModule,
  MenuModule,
  PlantCardOverviewModule,
  SelectedSystemSupportComponent,
  SystemDefinition,
} from 'ngx-zcs-blocks';
import gql from 'graphql-tag';
import { ApplyPipeModule, UiPromiseService } from 'ngx-zcs-components';
import { map } from 'rxjs/operators';

import { RefreshService } from '../../services';
import { SharedModule } from '../../shared.module';

const onlyPmOnVirtualPlant = (systemDefinition: SystemDefinition): boolean => {
  const devices = systemDefinition.devices.filter((device) => {
    const product = new ProductModelService(device.id);
    return product.type?.name !== 'connext' && product.type?.name !== 'azzurro-hub';
  });

  return devices.length > 0 && devices.every((device) => {
    const product = new ProductModelService(device.id);
    return product.isPowerMagic;
  });
};

@Component({
  standalone: true,
  imports: [
    AlertsCounterModule,
    ApplyPipeModule,
    CommonModule,
    HeaderOptionsModule,
    IonicModule,
    MatIconModule,
    MenuModule,
    PlantCardOverviewModule,
    RouterModule,
    SelectedSystemSupportComponent,
    SharedModule,
  ],
  selector: 'customer-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  @Input() toggleSidebar?: () => void;
  @Input() set systems(value: System[] | undefined | null) {
    this._systems$.next(value);
  }
  get systems(): System[] | undefined | null {
    return this._systems$.value;
  }

  @Input() set system(value: System | undefined | null) {
    this._system$.next(value);
  }
  get system(): System | undefined | null {
    return this._system$.value;
  }

  readonly systemDefinition$ = new BehaviorSubject<SystemDefinition | undefined>(undefined);

  get isAdvancedPlant(): boolean {
    return this.systemDefinition$.value?.source === 'aws';
  }

  get canBeReached(): boolean {
    const supported = [
      PlantMonitoringEnumType.CONNEXT_SOLARMAN,
      PlantMonitoringEnumType.SOLARMAN,
      PlantMonitoringEnumType.AZZURRO_HUB_SOLARMAN,
      PlantMonitoringEnumType.AZZURRO_HUB,
    ];

    const monitoringType = this.systemDefinition$.value?.loggersType;
    if (!monitoringType) {
      return false;
    }

    if (!this.isAdvancedPlant || !supported.includes(monitoringType)) {
      return false;
    }

    // AzzurroHub: plant management requires FW >= 900.600
    if (monitoringType === PlantMonitoringEnumType.AZZURRO_HUB) {
      const ahData = this.systemDefinition$.value?.data as Partial<AzzurroHubDataType> | undefined;
      return checkFwVersion(ahData?.info?.fw?.currVer, '900.600');
    }

    return true;
  }

  get currentSystemDevices(): System['devices'] | null {
    const devices = this.systemDefinition$.value?.devices || [];
    const metadata = this.systemDefinition$.value?.metadata;
    return metadata ? devices.filter((device) => {
      const product = new ProductModelService(device.id);
      return (
        product.type?.name === 'connext' ||
        product.type?.name === 'inverter' ||
        product.type?.name === 'meter' ||
        product.type?.name === 'enerclic' ||
        product.type?.name === 'evcharge' ||
        product.type?.name === 'azzurro-hub'
      );
    }) : null;
  }

  get hasMixedMonitoringType(): boolean {
    return this.systemDefinition$.value?.loggersType === 'CONNEXT_SOLARMAN'
      || this.systemDefinition$.value?.loggersType === 'AZZURRO_HUB_SOLARMAN';
  }

  get availableLoggersTypes(): ('connext' | 'solarman' | 'azzurroHub')[] {
    const loggersType = this.systemDefinition$.value?.loggersType;
    if (loggersType === 'CONNEXT_SOLARMAN') {
      return ['connext', 'solarman'];
    } else if (loggersType === 'AZZURRO_HUB_SOLARMAN') {
      return ['azzurroHub', 'solarman'];
    }
    return [];
  }

  get currentMonitoringType(): 'solarman' | 'connext' | 'azzurroHub' {
    return  this.systemDefinition$.value?.monitorPref === MonitorPrefEnumType.CONNEXT ? 'connext'
              : this.systemDefinition$.value?.monitorPref === MonitorPrefEnumType.AZZURRO_HUB ? 'azzurroHub' : 'solarman';
  }

  get areEvManaged(): boolean {
    if ( this.systemDefinition$.value?.monitoringType === 'AzzurroHub' ) {
      return (((this.systemDefinition$.value?.data) as AzzurroHubDataType)?.evCharger485?.length ?? 0) > 0
        || (((this.systemDefinition$.value?.data) as AzzurroHubDataType)?.evChargerCAN?.length ?? 0) > 0;
    }

    if ( this.systemDefinition$.value?.monitoringType === 'Connext' ) {
      return ((this.systemDefinition$.value?.data) as ConnextData)?.devices?.some((device) => {
        const product = new ProductModelService(device.sn);
        return product.type?.name === 'evcharge';
      });
    }
    return false;
  };

  get areHeatPumpManaged(): boolean {
    return this.systemDefinition$.value?.monitoringType === 'AzzurroHub';
  }

  get evchargesId(): string[] {
    if ( this.systemDefinition$.value?.monitoringType === 'AzzurroHub' ) {
      const evch485Sn = ((this.systemDefinition$.value?.data) as AzzurroHubDataType)?.evCharger485?.map((ev) => ev.sn) || [];
      const evchCANSn = ((this.systemDefinition$.value?.data) as AzzurroHubDataType)?.evChargerCAN?.map((ev) => ev.sn) || [];
      return [...evch485Sn, ...evchCANSn];
    }

    if ( this.systemDefinition$.value?.monitoringType === 'Connext' ) {
      return ((this.systemDefinition$.value?.data) as ConnextData)?.devices?.filter((device) => {
        const product = new ProductModelService(device.sn);
        return product.type?.name === 'evcharge';
      }).map((device) => device.sn) || [];
    }
    return [];
  }

  get heatPumpsId(): string[] {
    if ( this.systemDefinition$.value?.monitoringType === 'AzzurroHub' ) {
      return ((this.systemDefinition$.value?.data) as AzzurroHubDataType)?.heatPump?.map((hp) => hp.sn) || [];
    }
    return [];
  }

  readonly refresh$ = this.refreshService.refresh$;
  readonly _systems$ = new BehaviorSubject<System[] | undefined | null>(undefined);
  readonly _system$ = new BehaviorSubject<System | undefined | null>(undefined);

  readonly userHasSomeRole$ = this.roleService.has_somePermission$;

  readonly canEditEvchConfig$ = combineLatest([
    this.authService.user$,
    this.roleService.is_ZCS_Operator$,
    this.systemDefinition$,
  ]).pipe(
    map(([user, isOperator, systemDefinition]) => {
      const isAzzurroHubPlant = systemDefinition?.monitoringType === 'AzzurroHub';
      const userPlantRole = systemDefinition?.aws?.users?.find((u) => u.email === user?.email)?.userLevel;
      return isAzzurroHubPlant && (isOperator || (userPlantRole === UserPermissions.Admin || userPlantRole === UserPermissions.Owner));
    }),
  );

  readonly getDeviceIconIdentifier = getDeviceIconIdentifier;

  readonly queryParams$ = this.route.queryParams;

  readonly areEventsManaged$ = this.systemDefinition$.pipe(
    isNotNullOrUndefined(),
    map((systemDefinition) =>
      systemDefinition.source === 'aws'
      && !systemDefinition.configuration.connextId
      && !onlyPmOnVirtualPlant(systemDefinition)),
  );

  readonly areAlertsManaged$ = this.systemDefinition$.pipe(
    isNotNullOrUndefined(),
    map((systemDefinition) =>
      !systemDefinition.configuration.connextId
      && !(systemDefinition.monitoringType === 'Solarman' && onlyPmOnVirtualPlant(systemDefinition))),
  );

  constructor(
    private readonly uiPromiseService: UiPromiseService,
    private readonly updatePlantSettingsInCertifiedPlantMutation: UpdatePlantSettingsInCertifiedPlantMutation,
    private readonly roleService: RoleService,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthenticationService,
    readonly refreshService: RefreshService,
  ) {}

  async updateMonitoringPref(preference: 'connext' | 'solarman' | 'azzurroHub'): Promise<void> {
    const monitorPref = preference === 'connext' ? MonitorPrefEnumType.CONNEXT
      : preference === 'azzurroHub' ? MonitorPrefEnumType.AZZURRO_HUB
      : MonitorPrefEnumType.SOLARMAN;

    const plantId = this.system?.uid;
    const source = this.system?.source;
    if (source !== 'aws' || !plantId) {
      return;
    }

    const input: UpdatePlantSettingsInCertifiedPlantInput = {
      plantId,
      plantPref: {
        monitorPref,
      },
    };

    const query = gql`
    fragment certifiedplanttype on CertifiedPlantType {
      statusCode
      success
    }
    `;

    this.uiPromiseService.run(() => this.updatePlantSettingsInCertifiedPlantMutation.execute(query, input), {
      loading: true,
      loadingMessage: 'Updating monitoring preference...',
    });
  }
}
